import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { EuiFlexGrid } from "@elastic/eui";
import _ from "lodash";
import { useSelector } from "react-redux";
import { getUserRoles } from "modules/auth/slice";
import TripSection from "../../shared/TripSection";
import ClientDetails from "./ClientDetails";
import BranchDetails from "./BranchDetails";
import TripPickupTimeDetails from "./TripPickupTimeDetails";
import ContractTypeDetails from "./ContractTypeDetails";
import TripSourceDetails from "./TripSourceDetails";
import TripReturnableDetails from "./TripReturnableDetails";
import GoogleDistanceDetails from "./GoogleDistanceDetails.";
import CalculatedDistanceDetails from "./CalculatedDistanceDetails";
import ManualDistanceDetails from "./ManualDistanceDetails";
import HelpersCountDetails from "./HelpersCountDetails";
import ScalesCountDetails from "./ScalesCountDetails";
import TollStationGatesCount from "./TollStationGatesCount";
import PreTripDistance from "./PreTripDistance";
import TripTagDetails from "./TripTagDetails";
import TripTypeDetails from "./TripTypeDetails";
import TripCostDetails from "./TripCostDetails";
import TripAppVersionDetails from "./TripAppVersionDetails";
import TripGeofencingDetails from "./TripGeofencingDetails";
import TripCollectionShipmentAmounts from "./TripCollectionShipmentAmounts";
import ProjectDetails from "./ProjectDetails";
import TripAutoEndingDetails from "./TripAutoEndingDetails";

export default function TripDetails({
    clientName,
    branchName,
    contractTypeOptions,
    contractType,
    tripPickupTime,
    tripPickupTimeOptions,
    onTripPickupTimeChange,
    isPickupTimeTripEditable,
    onContractTypeChange,
    isContractTypeEditable,
    projectName,
    tripSourceOptions,
    tripSource,
    onTripSourceChange,
    isTripSourceEditable,
    tripReturnableOptions,
    isReturnable,
    onTripReturnableChange,
    isReturnableEditable,
    TripGeofencingOptions,
    isGeofencing,
    isTripGeofencingEditable,
    onTripGeofenceChange,
    autoEndingOptions,
    isAutoEnding,
    onAutoEndingChange,
    isAutoEndingEditable,
    TripCollectionShipmentAmountsOptions,
    isTripCollectionShipmentAmounts,
    onTripCollectionShipmentAmountsChange,
    isTripCollectionShipmentAmountsEditable,
    googleDistance,
    calculatedDistance,
    manualDistanceOptions,
    manualDistance,
    onManualDistanceChange,
    isManualDistanceEditable,
    helpersCountOptions,
    helpersCount,
    onHelpersCountChange,
    isHelpersCountEditable,
    scalesCountOptions,
    scalesCount,
    onScalesCountChange,
    isScalesCountEditable,
    tollStationGatesCountOptions,
    tollStationGatesCount,
    onTollStationGatesCountChange,
    isTollStationGatesCountEditable,
    preTripDistanceOptions,
    preTripDistance,
    onPreTripDistanceChange,
    isPreTripDistanceEditable,
    tags,
    tagsCount,
    onTagCountChange,
    isTagsCountEditable,
    tagCountOptions,
    tripTypeOptions,
    tripTypeName,
    onTripTypeChange,
    isTripTypeEditable,
    tripCostOptions,
    tripCost,
    onTripCostChange,
    isTripCostEditable,
    appVersion,
    organizationId,
    branchOptions,
    onBranchIdChange,
    isTripStatusPending,
}) {
    const intl = useIntl();
    const clientLabel = intl.formatMessage({ id: "trip.client" });
    const branchLabel = intl.formatMessage({ id: "trip.client-branch" });
    const contractTypeLabel = intl.formatMessage({ id: "trip.contract-type" });
    const projectNameLabel = intl.formatMessage({ id: "trip.project-name" });
    const pickupTimeLabel = intl.formatMessage({ id: "trip.pickup-time" });
    const tripSourceLabel = intl.formatMessage({ id: "trip.source" });
    const tripReturnableLabel = intl.formatMessage({ id: "trip.returnable" });
    const TripGeofencinglabel = intl.formatMessage({ id: "trip.geofencing" });
    const tripAutoEnding = intl.formatMessage({ id: "trip.auto-ending" });
    const TripCollectionShipmentAmountsLabel = intl.formatMessage({
        id: "trip.collectionShipmentAmounts",
    });
    const googleDistanceLabel = intl.formatMessage({
        id: "trip.google-distance",
    });
    const calculatedDistanceLabel = intl.formatMessage({
        id: "trip.calculated-distance",
    });
    const manualDistanceLabel = intl.formatMessage({
        id: "trip.manual-distance",
    });
    const helpersCountLabel = intl.formatMessage({ id: "trip.helpers-count" });
    const scalesCountLabel = intl.formatMessage({ id: "trip.scales-count" });
    const tollStationGatesCountLabel = intl.formatMessage({
        id: "trip.toll-station-gates-count",
    });
    const preTripDistanceLabel = intl.formatMessage({
        id: "trip.pre-trip-distance",
    });
    const tripTypeLabel = intl.formatMessage({ id: "trip.trip-type" });
    const tripCostLabel = intl.formatMessage({ id: "trip.trip-cost" });
    const tripAppVersionLabel = intl.formatMessage({ id: "trip.app-version" });
    const getCurrentUserRoles = useSelector(getUserRoles());
    const isUserSupplierPartner = getCurrentUserRoles?.includes("supplier_partner_account_manager");

    return (
        <TripSection
            headerTitle={<FormattedMessage id="trip.trip-details-section-title" />}
            headerIconType="apmTrace"
        >
            <EuiFlexGrid
                columns={3}
                gutterSize="l"
            >
                <ClientDetails
                    label={clientLabel}
                    clientName={clientName}
                    organizationId={organizationId}
                />
                <BranchDetails
                    label={branchLabel}
                    branchName={branchName}
                    branchOptions={branchOptions}
                    organizationId={organizationId}
                    onChange={onBranchIdChange}
                    isEditable={isTripStatusPending}
                />
                <TripTypeDetails
                    label={tripTypeLabel}
                    options={tripTypeOptions}
                    tripTypeName={tripTypeName}
                    onChange={onTripTypeChange}
                    isEditable={isTripTypeEditable}
                />
                <TripPickupTimeDetails
                    label={pickupTimeLabel}
                    options={tripPickupTimeOptions}
                    time={tripPickupTime}
                    onChange={onTripPickupTimeChange}
                    isEditable={isPickupTimeTripEditable}
                />
                <TripSourceDetails
                    label={tripSourceLabel}
                    options={tripSourceOptions}
                    source={tripSource}
                    onChange={onTripSourceChange}
                    isEditable={isTripSourceEditable}
                />
                {tripSource === "system" && (
                    <TripAppVersionDetails
                        label={tripAppVersionLabel}
                        appVersion={appVersion}
                    />
                )}
                <TripReturnableDetails
                    label={tripReturnableLabel}
                    options={tripReturnableOptions}
                    isReturnable={isReturnable}
                    onChange={(_data, successCallBack) =>
                        onTripReturnableChange(_data, successCallBack)
                    }
                    isEditable={isReturnableEditable}
                />
                <TripGeofencingDetails
                    label={TripGeofencinglabel}
                    options={TripGeofencingOptions}
                    isGeofencing={isGeofencing}
                    onChange={onTripGeofenceChange}
                    isEditable={isTripGeofencingEditable}
                />
                <TripAutoEndingDetails
                    label={tripAutoEnding}
                    options={autoEndingOptions}
                    isAutoEnding={isAutoEnding}
                    onChange={onAutoEndingChange}
                    isEditable={isAutoEndingEditable}
                />
                <TripCollectionShipmentAmounts
                    label={TripCollectionShipmentAmountsLabel}
                    options={TripCollectionShipmentAmountsOptions}
                    isTripCollectionShipmentAmounts={isTripCollectionShipmentAmounts}
                    onChange={onTripCollectionShipmentAmountsChange}
                    isEditable={isTripCollectionShipmentAmountsEditable}
                />
                <ContractTypeDetails
                    label={contractTypeLabel}
                    options={contractTypeOptions}
                    contractType={contractType}
                    onChange={onContractTypeChange}
                    isEditable={isUserSupplierPartner ? false : isContractTypeEditable}
                />
                {contractType === "monthly" && (
                    <ProjectDetails
                        label={projectNameLabel}
                        projectName={projectName}
                    />
                )}
                <GoogleDistanceDetails
                    label={googleDistanceLabel}
                    distance={googleDistance}
                />
                <CalculatedDistanceDetails
                    label={calculatedDistanceLabel}
                    distance={calculatedDistance}
                />
                <ManualDistanceDetails
                    label={manualDistanceLabel}
                    options={manualDistanceOptions}
                    distance={manualDistance}
                    onChange={onManualDistanceChange}
                    isEditable={isManualDistanceEditable}
                />
                <TripCostDetails
                    label={tripCostLabel}
                    options={tripCostOptions}
                    cost={tripCost}
                    onChange={onTripCostChange}
                    isEditable={isTripCostEditable}
                />
                <HelpersCountDetails
                    label={helpersCountLabel}
                    options={helpersCountOptions}
                    count={helpersCount}
                    onChange={onHelpersCountChange}
                    isEditable={isHelpersCountEditable}
                />
                <ScalesCountDetails
                    label={scalesCountLabel}
                    options={scalesCountOptions}
                    count={scalesCount}
                    onChange={onScalesCountChange}
                    isEditable={isScalesCountEditable}
                />
                <TollStationGatesCount
                    label={tollStationGatesCountLabel}
                    options={tollStationGatesCountOptions}
                    count={tollStationGatesCount}
                    onChange={onTollStationGatesCountChange}
                    isEditable={isTollStationGatesCountEditable}
                />
                <PreTripDistance
                    label={preTripDistanceLabel}
                    options={preTripDistanceOptions}
                    distance={preTripDistance}
                    onChange={onPreTripDistanceChange}
                    isEditable={isPreTripDistanceEditable}
                />
                {tags &&
                    tags.map((tag) => (
                        <TripTagDetails
                            label={tag + " Tag"}
                            options={tagCountOptions}
                            count={_.find(tagsCount, { tagName: tag }).count}
                            onChange={(e, successCallBack) =>
                                onTagCountChange(e.requestedCount, tag, successCallBack)
                            }
                            isEditable={isTagsCountEditable}
                        />
                    ))}
            </EuiFlexGrid>
        </TripSection>
    );
}
